import { formatPhoneNumberIntl } from 'react-phone-number-input'
import { colors } from '../styles/tokens'

const DAYS_SINCE_THRESHOLDS = {
  fresh: 30,
  stale: 90,
} as const

const DAYS_SINCE_HEX = {
  fresh: colors['success'],
  stale: colors['warning'],
  overdue: colors['error'],
} as const

type DaysSinceLevel = 'fresh' | 'stale' | 'overdue'

function getDaysSinceLevel(days: number): DaysSinceLevel {
  if (days < DAYS_SINCE_THRESHOLDS.fresh) return 'fresh'
  if (days < DAYS_SINCE_THRESHOLDS.stale) return 'stale'
  return 'overdue'
}

/**
 * Returns a Tailwind color class for a "days since update" indicator.
 * - < 30 days → success (green)
 * - 30–89 days → warning (orange)
 * - 90+ days → error (red)
 *
 * Classes are written as complete literals so Tailwind's JIT scanner can detect them.
 */
const DAYS_SINCE_CLASSES: Record<
  'bg' | 'border' | 'text',
  Record<DaysSinceLevel, string>
> = {
  bg: { fresh: 'bg-success', stale: 'bg-warning', overdue: 'bg-error' },
  border: {
    fresh: 'border-success',
    stale: 'border-warning',
    overdue: 'border-error',
  },
  text: { fresh: 'text-success', stale: 'text-warning', overdue: 'text-error' },
}

export const getDaysSinceColorClass = (
  days: number,
  prefix: 'bg' | 'border' | 'text' = 'bg'
): string => DAYS_SINCE_CLASSES[prefix][getDaysSinceLevel(days)]

/** Hex color variant for styled-components or inline styles. */
export const getDaysSinceHex = (days: number): string =>
  DAYS_SINCE_HEX[getDaysSinceLevel(days)]

export const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export const decimalUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const decimalOnly = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

export const formatUsd = (num?: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return usd.format(num)
}

export const formatUsdDecimal = (num: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return decimalUsd.format(num)
}

export const formatUsdSmartDecimal = (num: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return Number.isInteger(num) ? usd.format(num) : decimalUsd.format(num)
}

export const formatDecimalOnly = (num?: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return decimalOnly.format(num)
}

export const twoDecimalNum = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const integerNum = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export const percentageNum = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

export const formatPercentage = (num: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return percentageNum.format(num)
}

export const formatInteger = (num?: number): string => {
  if (num === null || num === undefined) {
    return ''
  }

  return integerNum.format(num)
}

export const formatTwoDecimal = (num: number): string => {
  if (num === null || num === undefined) {
    return ''
  }
  return twoDecimalNum.format(num)
}

export const formatPhoneNumber = ({
  phoneNumber,
  extension,
}: {
  phoneNumber: string
  extension?: string
}): string => {
  const phoneNumberWithPlus = phoneNumber.startsWith('+')
    ? phoneNumber
    : `+${phoneNumber}`
  let formattedPhoneNumber =
    formatPhoneNumberIntl(phoneNumberWithPlus) || phoneNumberWithPlus
  // This will return +1 213 373 4253
  // I want +1 (213) 373-4253
  const [countryCode, areaCode, firstThree, lastFour] =
    formattedPhoneNumber.split(' ')
  if (countryCode && areaCode && firstThree && lastFour) {
    formattedPhoneNumber = `${countryCode} (${areaCode}) ${firstThree}-${lastFour}`
  }
  return extension
    ? `${formattedPhoneNumber}, ext. ${extension}`
    : formattedPhoneNumber
}

/**
 * Generic helper to format a value or return a fallback for nullish values.
 * Unlike truthy checks, this correctly handles 0 as a valid value.
 */
export const formatOrFallback = <T>(
  value: T | null | undefined,
  formatter: (val: T) => string,
  fallback: string = '-'
): string =>
  value !== null && value !== undefined ? formatter(value) : fallback

export const integerFormatterOrFallback = (
  value: number | null | undefined,
  fallback: string = '-'
) => formatOrFallback(value, formatInteger, fallback)

export const percentFormatterOrFallback = (
  value: number | null | undefined,
  fallback: string = '-'
) => formatOrFallback(value, (v) => formatInteger(v) + '%', fallback)

export const usdFormatterOrFallback = (
  value: number | null | undefined,
  fallback: string = '-'
) => formatOrFallback(value, formatUsd, fallback)

// Date-only strings ('YYYY-MM-DD' from a Django DateField) parse as UTC
// midnight in JS, which rolls back a day in negative-UTC zones. Force UTC
// noon for those; full ISO datetimes (with their own offset) parse as-is.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

export const dateFormatterOrFallback = (
  value: string | null | undefined,
  fallback: string = '-'
) =>
  formatOrFallback(
    value,
    (v) => {
      const date = DATE_ONLY.test(v) ? new Date(`${v}T12:00:00Z`) : new Date(v)
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    },
    fallback
  )

// formatDateWithTimeAndMinutes formats a date into "MM/DD/YYYY HH:MM AM/PM" format
export const formatDateWithTimeAndMinutes = (date: Date | string) => {
  if (!(date instanceof Date)) {
    date = new Date(date)
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }
  return new Intl.DateTimeFormat('en-US', options).format(date)
}

// formatDateWithTimeAndMinutes2 formats a date into separate date and time strings
export const formatDateWithTimeAndMinutes2 = (date: Date | string) => {
  if (!(date instanceof Date)) {
    date = new Date(date)
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  }

  const formattedDate = new Intl.DateTimeFormat('en-US', dateOptions).format(
    date
  )
  const formattedTime = new Intl.DateTimeFormat('en-US', timeOptions).format(
    date
  )

  return {
    date: formattedDate,
    time: formattedTime,
  }
}

// formatDate formats a date string into "MMM DD, YYYY" format (e.g., "Jan 01, 2023")
export function formatDate(dateString?: string | null) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// formatDate2 formats a date string into "MM/DD/YYYY" format (e.g., "07/31/2025")
export function formatDate2(dateString?: string | null) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

// formatDateOnly formats a date-only string (YYYY-MM-DD) into "MM/DD/YYYY".
// Uses UTC noon to avoid timezone shift where UTC midnight rolls back a day
// in US timezones (e.g., "2026-03-17" becoming "03/16/2026").
export function formatDateOnly(dateString?: string | null) {
  if (!dateString) return '-'
  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0))
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

// Additional formatting functions for demographics and charts
export function formatCurrency(
  value: number,
  options?: { decimals?: boolean }
): string {
  const d = options?.decimals ?? true
  if (value >= 1000000000) {
    return `$${d ? (value / 1000000000).toFixed(1) : Math.round(value / 1000000000)}B`
  }
  if (value >= 1000000) {
    return `$${d ? (value / 1000000).toFixed(1) : Math.round(value / 1000000)}M`
  }
  if (value >= 1000) {
    return `$${d ? (value / 1000).toFixed(1) : Math.round(value / 1000)}K`
  }
  return `$${value.toLocaleString()}`
}

export function formatPercentageValue(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

export function formatPopulationDensity(value: number): string {
  return `${Math.round(value)} ppl / mile²`
}

export function formatVolume(value: number, unit: string): string {
  return `${value.toLocaleString()} ${unit}`
}

/**
 * Format an address from its component parts.
 * Filters out null/undefined values and joins with commas.
 */
export function formatAddress(parts: {
  street?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
}): string | null {
  const addressParts = [
    parts.street,
    parts.city,
    parts.state,
    parts.zip,
  ].filter(Boolean)
  return addressParts.length > 0 ? addressParts.join(', ') : null
}

/**
 * Format a date string as a human-readable relative time (e.g. "5m ago", "3h ago", "2d ago").
 * Falls back to "MMM D" format for dates older than 7 days.
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Capitalize the first letter of a string.
 *
 * @param str - The string to capitalize
 * @returns The string with its first letter capitalized
 *
 * @example
 * capitalizeFirst('select products') // => 'Select products'
 * capitalizeFirst('Company') // => 'Company'
 */
export function capitalizeFirst(str: string): string {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Derive up to two uppercase initials from a person's name (e.g. avatar chips).
 * Falls back to "UN" (unknown) when no usable letters are present.
 */
export function getInitials(name: string): string {
  return (
    name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'UN'
  )
}
